import * as React from 'react';
import { find } from 'lodash';
import * as moment from 'moment';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import bookingStatuses from '../../data/bookingStatuses';
import paymentStatuses from '../../data/paymentStatuses';
import {
  Box,
  Tag,
  Icon,
  CompanyAvatar,
  DriverAvatar,
  Tooltip,
  DateTime,
} from '..';
import IconAvatar from '../IconAvatar/IconAvatar';

interface Props {
  booking: any;
  vehicle?: any;
  duration?: number;
  companies: any;
  vehicleTypes: any;
  vehicles: any;
}

class BookingSidebar extends React.Component<Props> {
  contact: any;
  vehicleType: any;
  vehicle?: any;
  duration: number;

  constructor(props) {
    super(props);

    // Determine contact details to show
    if (this.isCompanyBooking) {
      this.contact = find(this.props.companies, {
        id: Number(this.props.booking.company_id),
      });
    } else if (
      this.props.booking.drivers !== undefined &&
      this.props.booking.drivers.length > 0
    ) {
      this.contact = this.props.booking.drivers[0];
    }

    // Determine vehicle details to show
    this.vehicle = this.props.vehicle || this.props.booking.vehicle;
    if (!this.props.vehicle && this.props.booking.vehicle_id) {
      this.vehicle = find(this.props.vehicles, {
        id: Number(this.props.booking.vehicle_id),
      });
    }

    // Determine vehicle type details to show
    this.vehicleType = undefined;
    if (this.vehicle) {
      this.vehicleType = this.vehicle.vehicle_type;
    }
    if (!this.vehicleType) {
      this.vehicleType = find(this.props.vehicleTypes, {
        id: Number(this.props.booking.vehicle_type_id),
      });
    }

    // Determine vehicle duration
    this.duration = this.props.duration;
    if (!this.duration) {
      this.duration =
        1 +
        moment(this.props.booking.end).diff(
          moment(this.props.booking.start),
          'd',
        );
    }
  }

  get isCompanyBooking() {
    return !!this.props.booking.company_id;
  }

  render() {
    const booking = this.props.booking;

    // Determine booking status
    const bookingStatus = find(bookingStatuses, {
      value: booking.status,
    });

    // Determine payment status
    let paymentStatus: any = paymentStatuses[0];
    if (booking.payment_status) {
      paymentStatus = find(paymentStatuses, { value: booking.payment_status });
    }

    const totalOutstanding = Number(
      (Number(booking.total_cost) - Number(booking.total_paid)).toFixed(2),
    );

    return (
      <Box>
        <div className="media">
          <div className="media-left has-text-centered">
            <span className="avatar image is-64x64">
              <img src={this.vehicleType.image} />
            </span>
            {this.vehicle && (
              <Tag type="warning has-text-weight-bold">
                {this.vehicle.registration}
              </Tag>
            )}
          </div>
          <div className="media-content">
            <small className="has-text-grey">Hire</small>
            <br />
            <strong>
              {this.vehicleType.name} for {this.duration} day
              {this.duration !== 1 && 's'}
            </strong>
            <br />
            <small>
              <Icon icon="calendar-o has-text-grey-light" />{' '}
              {moment(booking.start).format('DD/MM/YY [at] HH:mm')} -{' '}
              {moment(booking.end).format('DD/MM/YY')}
            </small>
            {this.vehicle && (
              <>
                <br />
                <small>
                  <Icon icon="truck has-text-grey-light" />{' '}
                  {booking.mileage_end || this.vehicle.mileage} miles
                </small>
              </>
            )}
          </div>
          <div className="media-right">
            <Tooltip text={bookingStatus.tooltip}>
              <Tag type={bookingStatus.class}>
                {bookingStatus.name.toUpperCase()}
              </Tag>
            </Tooltip>
          </div>
        </div>
        <div className="media">
          <div className="media-left">
            {booking.company_id ? (
              <Link to={`/companies/${booking.company_id}`}>
                <CompanyAvatar company={{ id: booking.company_id }} />
              </Link>
            ) : this.contact ? (
              <Link to={`/drivers/${this.contact.id}`}>
                <DriverAvatar driver={{ id: this.contact.id }} />
              </Link>
            ) : (
              <IconAvatar icon="question" colour="#eeeeee" />
            )}
          </div>
          <div className="media-content">
            <small className="has-text-grey">Customer</small>
            <br />
            <strong>{booking.contact_name || 'Unknown Contact'}</strong>
            <br />
            {!!booking.contact_phone_number && (
              <>
                <small>
                  <Icon icon="phone has-text-grey-light" />{' '}
                  {booking.contact_phone_number}
                </small>
                <br />
              </>
            )}
            {!!booking.contact_email && (
              <>
                <small>
                  <Icon icon="envelope has-text-grey-light" />{' '}
                  {booking.contact_email}
                </small>
                <br />
              </>
            )}
          </div>
          <div className="media-right">
            <Tag type={booking.company_id ? 'primary' : 'info'}>
              {booking.company_id ? 'COMPANY' : 'INDIVIDUAL'}
            </Tag>
          </div>
        </div>
        <div className="media">
          <figure className="media-left">
            <span
              className={`avatar image is-64x64 has-text-${
                Number(totalOutstanding.toFixed(2)) > 0 ? 'danger' : 'success'
              }`}
            >
              <Icon
                icon={
                  Number(totalOutstanding.toFixed(2)) > 0
                    ? 'exclamation-triangle'
                    : 'check'
                }
                size="large"
              />
            </span>
          </figure>
          <div className="media-content">
            <small className="has-text-grey">Outstanding Cost</small>
            <br />
            <strong>£{totalOutstanding.toFixed(2)}</strong>
            <br />
            {Number(booking.total_paid) > 0 && (
              <small>
                <Icon icon="shopping-cart has-text-grey-light" /> £
                {Number(booking.total_cost).toFixed(2)} in total
                <br />
                <Icon icon="share has-text-grey-light" /> £
                {Number(booking.total_paid).toFixed(2)} paid
              </small>
            )}
          </div>
          <div className="media-right">
            <Tooltip text={paymentStatus.tooltip}>
              <Tag type={paymentStatus.class}>
                {paymentStatus.name.toUpperCase()}
              </Tag>
            </Tooltip>
          </div>
        </div>
        <br />
        <table>
          <tbody>
            <tr>
              <td>Invoice Number</td>
              <td className="has-text-right">
                <Tag type="dark">{booking.invoiceId}</Tag>
              </td>
            </tr>
            {booking.order_number !== '' &&
              booking.order_number !== null &&
              booking.order_number !== 'null' && (
                <tr>
                  <td>Order Number</td>
                  <td className="has-text-right">{booking.order_number}</td>
                </tr>
              )}
            <tr>
              <td>Created by {booking.created_by}</td>
              <td className="has-text-right">
                <DateTime date={booking.created_at} />
              </td>
            </tr>
            {booking.updated_at && (
              <tr>
                <td>Last updated by {booking.updated_by}</td>
                <td className="has-text-right">
                  <DateTime date={booking.updated_at} />
                </td>
              </tr>
            )}
            {booking.deleted_at && (
              <tr>
                <td>Removed</td>
                <td className="has-text-right">
                  <DateTime date={booking.deleted_at} />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Box>
    );
  }
}

const mapStateToProps = ({ companies, vehicleTypes, vehicles }) => ({
  companies: companies.companies,
  vehicleTypes: vehicleTypes.vehicleTypes,
  vehicles: vehicles.vehicles,
});

export default connect(mapStateToProps)(BookingSidebar);
